/* eslint-disable max-classes-per-file */
import * as React from "react";
import { connect } from "react-redux";
import { Table } from "react-bootstrap";
import { FormattedDate } from "react-intl";
import { filter, toPairs, sortBy } from "lodash";
import { Button } from "carbon-components-react";
import { CurrencyDollar16 } from "@carbon/icons-react";

import { AppShape, ExplanationShape, HurdleShape } from "@eave/shared/types/api";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import AdditionalIncome from "../../AdditionalIncome";
import { ExplanationType } from "../../Explanations";
import Money from "../../Money";
import { loadApp } from "../actions";
import { getFileDownloadBaseURL } from "../../../config";
import { getBorrowerEntityAsString } from "@eave/shared/utils/borrowers";

const humanize = require("underscore.string/humanize");
const FontAwesome = require("react-fontawesome");

/* ExplanationTransactions */

// TODO consolidate with AssetSummary and IncomeDocumentsReview transactions and perhaps
//      with explanations and document_summary on the borrower side
interface ExplanationTransactionsProps {
  app: AppShape;
  explanation: ExplanationShape;
  expandAll?: boolean;
}

export class ExplanationTransactions extends React.Component<ExplanationTransactionsProps, any> {
  constructor(props) {
    super(props);
    this.state = {
      showTransactions: props.expandAll,
      accountNameMap: ExplanationTransactions.getAccountNameMap(props.app),
    };

    this.toggleTransactions = this.toggleTransactions.bind(this);
  }

  // TODO an account name map could be passed in for reuse across multiple transaction components
  static getAccountNameMap(app) {
    const accountNameMap = Object.create(null); // init hashmap
    for (let i = 0; i < app.accounts.length; i += 1) {
      const { id, institution } = app.accounts[i];
      accountNameMap[id] = institution;
    }
    return accountNameMap;
  }

  toggleTransactions() {
    this.setState({ showTransactions: !this.state.showTransactions });
  }

  renderTransactions() {
    const sorted = sortBy(this.props.explanation.transactions, (t: any) => parseInt(t.id, 10));

    return (
      <Table condensed>
        <thead />
        <tbody>
          {sorted.map(t => {
            const transactionType = t.eave_transaction_type
              ? humanize(t.eave_transaction_type)
              : "type not detected";
            return (
              <tr key={t.id}>
                <td>{this.state.accountNameMap[t.account_id]}</td>
                <td>
                  <FormattedDate value={t.date} timeZone="UTC" />
                </td>
                <td>{t.memo}</td>
                <td>
                  <Money value={t.value} />
                </td>
                <td>{transactionType}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  render() {
    if (this.props.explanation.transactions.length === 0) {
      return null;
    }

    return (
      <div>
        <a onClick={this.toggleTransactions}>Transactions</a>
        {this.state.showTransactions && this.renderTransactions()}
      </div>
    );
  }
}

/* ExplanationsHurdleEditor */

interface HurdleEditorProps {
  dispatch?: Function;
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
}

export class ExplanationsHurdleEditor extends React.Component<HurdleEditorProps, any> {
  constructor(props) {
    super(props);
    this.state = {
      showIncomeLOEsOnly: false,
      showAdditionalIncomeModal: false,
      selectedAdditionalIncomeID: null,
    };

    this.createAdditionalIncome = this.createAdditionalIncome.bind(this);
    this.hideAdditionalIncomeModal = this.hideAdditionalIncomeModal.bind(this);
    this.toggleShowIncomeLOEsOnly = this.toggleShowIncomeLOEsOnly.bind(this);
  }

  static initExplanationStatusMap(props) {
    const statusMap = Object.create(null);
    const { explanations } = props.app;
    for (let i = 0; i < explanations.length; i += 1) {
      statusMap[explanations[i].id] = explanations[i].status;
    }
    return statusMap;
  }

  hideAdditionalIncomeModal() {
    this.setState({
      showAdditionalIncomeModal: false,
      selectedAdditionalIncomeID: null,
    });
    // values might have changed if this is a save
    loadApp(this.props.dispatch, this.props.app.id);
  }

  editAdditionalIncome(additionalIncomeID) {
    this.setState({
      showAdditionalIncomeModal: true,
      selectedAdditionalIncomeID: additionalIncomeID,
    });
  }

  createAdditionalIncome() {
    // id will be null
    this.setState({
      showAdditionalIncomeModal: true,
    });
  }

  toggleShowIncomeLOEsOnly() {
    this.setState({ showIncomeLOEsOnly: !this.state.showIncomeLOEsOnly });
  }

  explanations(): any {
    const { explanations } = this.props.app;
    if (!this.state.showIncomeLOEsOnly) {
      return explanations;
    }
    const incomeTypes = [
      ExplanationType.PayrollDeposits,
      ExplanationType.MatchedPayrollDeposits,
      ExplanationType.LargeUnaccountedDeposit,
      ExplanationType.LargeUnaccountedDepositNoPayroll,
    ];
    return filter(explanations, explanation => incomeTypes.includes(explanation.type_key));
  }

  render() {
    const { app, hurdle, onHide } = this.props;
    const { showAdditionalIncomeModal, selectedAdditionalIncomeID } = this.state;

    if (showAdditionalIncomeModal) {
      return (
        <AdditionalIncome
          application={app}
          additionalIncomeID={selectedAdditionalIncomeID}
          onHide={this.hideAdditionalIncomeModal}
        />
      );
    }

    let showHide = "Show only Income LOEs";
    if (this.state.showIncomeLOEsOnly) {
      showHide = "Show all types of explanations";
    }

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        <div className="doc-entry__modal-body">
          <div className="container-fluid">
            {this.renderAdditionalIncome()}
            <br />
            <br />
            <a onClick={this.toggleShowIncomeLOEsOnly}>{showHide}</a>
            <br />
            <br />
            {this.renderExplanations()}
          </div>
        </div>
      </HurdleView>
    );
  }

  renderAdditionalIncome() {
    const additionalIncomes: any = this.props.app.additional_incomes;
    return (
      <div>
        <h4>Additional Income</h4>
        <Table>
          <thead />
          <tbody>
            {additionalIncomes.map(ai => (
              <tr key={ai.id}>
                <td>{getBorrowerEntityAsString(ai.borrower_entity)}</td>
                <td>{ai.description}</td>
                <td>
                  <Money value={ai.monthly_income} />
                </td>
                <td>
                  <a onClick={this.editAdditionalIncome.bind(this, ai.id)}>
                    <FontAwesome name="edit" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Button renderIcon={CurrencyDollar16} onClick={this.createAdditionalIncome}>
          Enter new additional income
        </Button>
      </div>
    );
  }

  static findS3KeyForExplanation(explanationID, docs) {
    // TODO returns only first one, figure out multiple
    for (let i = 0; i < docs.length; i += 1) {
      const doc = docs[i];
      if (doc.owner_entity_type === "explanation" && doc.owner_entity_id === explanationID) {
        return doc.s3_key;
      }
    }
    return null;
  }

  static extractResponse(explanation, docs) {
    if (!explanation.response) {
      return "";
    }

    const responseObject = JSON.parse(explanation.response);
    // there are also structured responses that won't fit this,
    // but for this particular case they are all simple
    let simpleResponseValue = responseObject[explanation.type_key];

    if (explanation.explanation_type.response_type === "select") {
      const responseOptions = explanation.explanation_type.response_options;
      const selectResponse: any = responseOptions.find(o => o.key === simpleResponseValue);
      simpleResponseValue = selectResponse.display_text;
    } else if (explanation.explanation_type.response_type === "upload") {
      const s3Key = ExplanationsHurdleEditor.findS3KeyForExplanation(explanation.id, docs);
      if (s3Key !== null) {
        return <a href={getFileDownloadBaseURL(s3Key)}> Letter </a>;
      }
      return <p>Letter not found</p>;
    }

    delete responseObject[explanation.type_key];
    let otherElements = toPairs(responseObject);

    // TODO hack for demo, better formatting for this particular response
    if (simpleResponseValue === "real_estate_sale") {
      otherElements = [["Address", otherElements[0][1]]];
    }

    // TODO the list is an ugly hack for structured answers; make pretty
    //      the right fix will also read the explanation structure to get the display_text
    //      and correct ordering of the elements
    return (
      <ul style={{ listStyle: "none" }}>
        <li>{simpleResponseValue}</li>
        {otherElements &&
          otherElements.map((elementPair, index) => (
            <li key={index}>
              {elementPair[0]}: {elementPair[1]}
            </li>
          ))}
      </ul>
    );
  }

  renderExplanations() {
    return (
      <div>
        <h4>Letters of Explanation</h4>
        <Table striped bordered condensed>
          <thead>
            <tr>
              <th>Explanation type</th>
              <th>Explanation message</th>
              <th>Borrower response</th>
            </tr>
          </thead>
          <tbody>
            {this.explanations().map((explanation, index) => {
              return [
                <tr key={index * 2}>
                  <td>{explanation.type_key}</td>
                  <td>{explanation.message || explanation.explanation_type.display_text}</td>
                  <td>
                    {ExplanationsHurdleEditor.extractResponse(
                      explanation,
                      this.props.app.documents,
                    )}
                  </td>
                </tr>,
                <tr key={index * 2 + 1}>
                  <td colSpan={4}>
                    <ExplanationTransactions
                      app={this.props.app}
                      explanation={explanation}
                      expandAll
                    />
                  </td>
                </tr>,
              ];
            })}
          </tbody>
        </Table>
      </div>
    );
  }
}

export default connect()(ExplanationsHurdleEditor);
